import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const SuperadminLeads = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');

    const [leads, setLeads] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchLeads = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_leads`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (data.success) {
                setLeads(data.leads);
            } else {
                setError(data.message || 'Failed to fetch leads');
            }
        } catch (err) {
            setError('Error connecting to dashboard server');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLead = async (leadId) => {
        if (!window.confirm('Are you sure you want to delete this lead?')) return;
        setError('');
        setSuccessMessage('');
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=delete_lead`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ lead_id: leadId })
            });
            const data = await res.json();
            if (data.success) {
                setSuccessMessage(data.message || 'Lead deleted successfully');
                fetchLeads();
            } else {
                setError(data.message || 'Failed to delete lead');
            }
        } catch (err) {
            setError('Error connecting to dashboard server');
        }
    };

    const filteredLeads = leads.filter(lead =>
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm) ||
        lead.message.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <SuperAdminLayout>
            <div className="sa-header-area">
                <div>
                    <h2 className="sa-page-title">Visitor Contact Leads</h2>
                    <p className="sa-page-subtitle">View and manage form submissions from the landing page contact form.</p>
                </div>
            </div>

            {error && <div className="sa-alert sa-alert-error" style={{ marginBottom: '1.5rem' }}><i className="fas fa-exclamation-circle"></i> {error}</div>}
            {successMessage && <div className="sa-alert sa-alert-success" style={{ marginBottom: '1.5rem' }}><i className="fas fa-check-circle"></i> {successMessage}</div>}

            <div className="sa-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                    <div className="sa-search-box" style={{ margin: 0, flex: 1, maxWidth: '400px' }}>
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search by name, email, phone, message..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="sa-input"
                            style={{ width: '100%' }}
                        />
                    </div>
                    <button className="sa-secondary-btn" onClick={fetchLeads}><i className="fas fa-sync"></i> Refresh</button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <i className="fas fa-spinner fa-spin fa-2x" style={{ color: 'var(--primary-color)' }}></i>
                        <p style={{ marginTop: '10px', color: 'var(--text-secondary)' }}>Loading leads...</p>
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        <i className="fas fa-inbox fa-3x" style={{ marginBottom: '15px', opacity: 0.3 }}></i>
                        <p>No contact leads found.</p>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Visitor Details</th>
                                    <th>Message</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLeads.map((lead) => (
                                    <tr key={lead.id}>
                                        <td style={{ whiteSpace: 'nowrap', verticalAlign: 'top', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                            {new Date(lead.created_at).toLocaleDateString(undefined, {
                                                year: 'numeric', month: 'short', day: 'numeric',
                                                hour: '2-digit', minute: '2-digit'
                                            })}
                                        </td>
                                        <td style={{ verticalAlign: 'top', minWidth: '220px' }}>
                                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lead.name}</div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                <i className="fas fa-envelope" style={{ marginRight: '6px', width: '14px' }}></i>
                                                <a href={`mailto:${lead.email}`} style={{ color: 'inherit' }}>{lead.email}</a>
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                <i className="fas fa-phone" style={{ marginRight: '6px', width: '14px' }}></i>
                                                <a href={`https://wa.me/${lead.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                                    {lead.phone}
                                                </a>
                                            </div>
                                        </td>
                                        <td style={{ verticalAlign: 'top', whiteSpace: 'pre-wrap', color: 'var(--text-primary)', lineHeight: '1.5', minWidth: '300px' }}>
                                            {lead.message}
                                        </td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'top' }}>
                                            <button
                                                className="sa-action-btn delete"
                                                onClick={() => handleDeleteLead(lead.id)}
                                                title="Delete Lead"
                                                style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
};

export default SuperadminLeads;
